import { graphql } from '~/graphql/generated/gql';

/**
 * The coach v2 wire: one read, one feed, and commands that hand back acks.
 *
 * `seq` is selected on the interface rather than on each member — that is the whole reason
 * `CoachSessionEvent` is an interface, so a client can order and gap-check an event it has
 * no fragment for.
 */
export const COACH_EVENT_FRAGMENT = graphql(`
  fragment coachEventFields on CoachSessionEvent {
    __typename
    id
    seq
    clientRequestId
    ... on CoachAssistantChatResponseEvent {
      eventData {
        parts {
          __typename
          ... on CoachAssistantMessagePart { text }
          ... on CoachAssistantRecommendationsPart {
            set {
              __typename
              ... on CoachJobRecommendationSet {
                recommendations { id name imageUrl wage isSaved }
              }
              ... on CoachInstitutionRecommendationSet {
                recommendations { id name imageUrl heroImageUrl isSaved }
              }
              ... on CoachContentRecommendationSet {
                recommendations {
                  __typename
                  ... on CoachLearningAssetRecommendation {
                    id name slug imageUrl learningAssetType isCompleted duration coins isSaved
                  }
                  ... on CoachLearningAssetCollectionRecommendation {
                    id name slug imageUrl collectionType completedCount totalCount partnerLogoUrl partnerName duration coins isSaved
                  }
                }
              }
              ... on CoachActionRecommendationSet {
                recommendations { id name navigation }
              }
              ... on CoachIndustryRecommendationSet {
                recommendations { id name imageUrl isSaved }
              }
              ... on CoachSurveyRecommendationSet {
                recommendations { id name coins partnerLogoUrl partnerName externalLaunchPath contextType }
              }
            }
          }
          ... on CoachAssistantCrisisPart {
            country
            emphasiseEmergency
            emergency { slug name phone sms smsKeyword url emergency }
            services { slug name phone sms smsKeyword url emergency }
          }
        }
        askUserQuestion {
          __typename
          ... on CoachPlainQuestion { question selectType options { id label } }
          ... on CoachEndSessionQuestion { question options { id label role } }
          ... on CoachGoalSwitchQuestion {
            question
            options { id label role }
            newGoalTitle
            newGoal
            handoff
            provenance
          }
          ... on CoachGoalSetQuestion { question options { id label role } }
          ... on CoachStartersQuestion { question assignedLessonsTotal options { id label subtitle icon school } }
        }
      }
    }
    ... on CoachUserChatInputEvent {
      eventData { userMessage inputType }
    }
    ... on CoachToolCallEvent {
      eventData { toolName toolCallId arguments round callIndex callsInRound }
    }
    ... on CoachToolResponseEvent {
      eventData { toolName toolCallId toolResponse: response round callIndex callsInRound durationMs }
    }
    ... on CoachQuestionAnswerEvent {
      eventData { questionEventId selectedOptionIds selectedLabels role }
    }
    ... on CoachSessionStartersEvent {
      eventData {
        question {
          __typename
          ... on CoachPlainQuestion { question selectType options { id label } }
          ... on CoachEndSessionQuestion { question options { id label role } }
          ... on CoachGoalSwitchQuestion {
            question
            options { id label role }
            newGoalTitle
            newGoal
            handoff
            provenance
          }
          ... on CoachGoalSetQuestion { question options { id label role } }
          ... on CoachStartersQuestion { question assignedLessonsTotal options { id label subtitle icon school } }
        }
      }
    }
    ... on CoachActivityNavigationEvent {
      eventData { activity activityArgs }
    }
    ... on CoachActivityInfoEvent {
      eventData { infoType activityNavigationEventId }
    }
    ... on CoachGoalChangedEvent {
      eventData { kind goalTitle previousGoalTitle }
    }
  }
`);

export const COACH_SESSION_STATE_FRAGMENT = graphql(`
  fragment coachSessionStateFields on CoachSessionState {
    id
    status
    revision
    goalTitle
    goal
    goalAchievedAt
    goalSchoolName
    lessonId
    switchedFromSessionId
    openQuestion { id kind state }
    availableActions { id kind label }
    activityClosedSeq
  }
`);

/** The single resync primitive. No cursor opens a tab; a cursor reads the tail after that seq. */
export const COACH_SESSION_QUERY = graphql(`
  query coachSession($sessionId: ID, $cursor: Int) {
    coachSession(sessionId: $sessionId, cursor: $cursor) {
      seq
      turnInFlight
      session { ...coachSessionStateFields }
      events { ...coachEventFields }
    }
  }
`);

/**
 * The student's sessions, newest first — rows, not logs. Read on its own rather than folded into
 * the snapshot: it answers what a session was about and how it finished, which is a different
 * question from what the live one will accept next, and it is re-read on the actions that change it
 * rather than on every frame.
 */
export const COACH_SESSION_HISTORY_QUERY = graphql(`
  query coachSessionHistory {
    coachSessionHistory {
      id
      status
      createdAt
      goalTitle
      goal
      goalAchievedAt
      endReason
      endedAt
      summary
      switchedFromSessionId
    }
  }
`);

export const COACH_FEED_SUBSCRIPTION = graphql(`
  subscription coachFeed($userId: ID!) {
    coachFeed(userId: $userId) {
      sessionId
      kind
      seq
      event { ...coachEventFields }
      state { ...coachSessionStateFields }
      progress { toolName phase round callIndex callsInRound }
    }
  }
`);

export const COACH_START_SESSION_MUTATION = graphql(`
  mutation coachStartSession {
    coachStartSession {
      seq
      turnInFlight
      opened
      spoke
      session { ...coachSessionStateFields }
      events { ...coachEventFields }
    }
  }
`);

/**
 * The lesson entry. Unlike every other command here it answers with a snapshot rather than an ack,
 * because there may be no session yet for a feed frame to arrive on: the session it opens is born
 * holding the lesson's goal, and this read is the client's first sight of it.
 *
 * Tapping into the lesson already open is a no-op that answers with the session already open, so a
 * reload of the entry costs nothing and the caller may fire it without knowing which of the two it
 * will get. Which of the two it was is `opened`, on the same terms the plain start reads it: the
 * server's own word, because a birth and a no-op look identical from a binding the entry sets either
 * way, and the funnel this lane is measured in counts only the one that opened something.
 */
export const COACH_START_LESSON_SESSION_MUTATION = graphql(`
  mutation coachStartLessonSession($lessonId: ID!) {
    coachStartLessonSession(lessonId: $lessonId) {
      seq
      turnInFlight
      opened
      spoke
      session { ...coachSessionStateFields }
      events { ...coachEventFields }
    }
  }
`);

/**
 * Everything the student's school has set them, outstanding work first and finished work behind it.
 *
 * One query serving two callers: the page that lists them, and the nav item that only wants to know
 * whether there is anything to list. They share a `useAsyncData` key so the presence check costs
 * nothing beyond the read the page was making anyway.
 */
export const COACH_ASSIGNED_LESSONS_QUERY = graphql(`
  query coachAssignedLessons {
    core {
      user {
        self {
          id
          assignedLessons {
            id
            name
            school
            completed
            assigned
            imageUrl
            completedCount
            totalCount
            duration
            coins
          }
        }
      }
    }
  }
`);

export const COACH_SEND_MESSAGE_MUTATION = graphql(`
  mutation coachSendMessage($input: CoachSendMessageInput!) {
    coachSendMessage(input: $input) {
      conflict
      acceptedSeq
      revision
      latestSeq
    }
  }
`);

export const COACH_ANSWER_QUESTION_MUTATION = graphql(`
  mutation coachAnswerQuestion($input: CoachAnswerQuestionInput!) {
    coachAnswerQuestion(input: $input) {
      conflict
      acceptedSeq
      revision
      latestSeq
    }
  }
`);

export const COACH_END_SESSION_MUTATION = graphql(`
  mutation coachEndSession($input: CoachEndSessionInput!) {
    coachEndSession(input: $input) {
      conflict
      acceptedSeq
      revision
      latestSeq
    }
  }
`);

export const COACH_RECORD_ACTIVITY_NAVIGATION_MUTATION = graphql(`
  mutation coachRecordActivityNavigation($input: CoachRecordActivityNavigationInput!) {
    coachRecordActivityNavigation(input: $input) {
      applied
      conflict
      acceptedSeq
      revision
      latestSeq
    }
  }
`);

export const COACH_RECORD_ACTIVITY_EVENT_MUTATION = graphql(`
  mutation coachRecordActivityEvent($input: CoachRecordActivityEventInput!) {
    coachRecordActivityEvent(input: $input) {
      applied
      conflict
      acceptedSeq
      revision
      latestSeq
    }
  }
`);

export const COACH_END_ACTIVITY_SESSION_MUTATION = graphql(`
  mutation coachEndActivitySession($input: CoachEndActivitySessionInput!) {
    coachEndActivitySession(input: $input) {
      conflict
      acceptedSeq
      revision
      latestSeq
    }
  }
`);

/**
 * The test harness's reset surface, not part of the coach protocol: these take sessions away rather
 * than moving them forward. Gated server-side by `@requiresDevMode`, so a refused caller gets an
 * error rather than a reset that quietly did nothing.
 */
export const COACH_RESET_USER_MUTATION = graphql(`
  mutation coachResetUser {
    coachResetUser {
      deletedSessions
      deletedSavedItems
      deletedConsiderationCompletions
      deletedProgressEntries
      deletedWalletItems
    }
  }
`);

export const COACH_RESET_CURRENT_SESSION_MUTATION = graphql(`
  mutation coachResetCurrentSession {
    coachResetCurrentSession {
      deletedSessions
    }
  }
`);
